import { initializeApp, type FirebaseApp } from '@firebase/app';
import { getAuth, signInWithCustomToken } from '@firebase/auth';
import { collection, enableIndexedDbPersistence, getFirestore, onSnapshot } from '@firebase/firestore';
import { useCallback, useEffect } from 'react';
import { atom, selector, useRecoilValue, useSetRecoilState } from 'recoil';
import { useAuthenticated } from '../auth';
import { missionsServer, platform, version } from '../config';
import { Action, Identifiable, MapElement, Mission, MissionEvent, MissionsApi, Patient, Report, Resource, Section, UserInfo, UserMissionGroup } from './api';
import { log, statePrefix } from './config';
import { MissionsClient } from './missions';
import { useSetMissionEvents, useSetMissions } from './state';
import type { FullMission, SectionResources, SectionWithResources } from './types';

export type MissionsState = { state: 'disabled' } | { state: 'loading' } | { state: 'failed'; error: unknown } | { state: 'ready' };

const missionsStateAtom = atom<MissionsState>({
  key: `${statePrefix}:state`,
  default: { state: 'loading' },
});

export function useMissionsState() {
  return useRecoilValue(missionsStateAtom);
}

export type MissionsPermission = 'read' | 'create' | 'update';

export type MissionsGroupPermissions = {
  groupId: string;
  permission: MissionsPermission;
};

export type MissionsContext = {
  apiKey: string;
  user: UserInfo;
  firebase: FirebaseApp;
};

const missionsContextAtom = atom<MissionsContext | undefined>({
  key: `${statePrefix}:context`,
  default: undefined,
});

const missionsClientSelector = selector<MissionsClient | undefined>({
  key: `${statePrefix}:client`,
  get: ({ get }) => {
    const context = get(missionsContextAtom);
    if (context) {
      return new MissionsClient(createMissionsApi(context.apiKey));
    }
  },
});

export function useMissionsClient() {
  const client = useMissionsClientMaybe();
  if (!client) throw new Error('Missions client not initialized!');
  return client;
}

export function useMissionsClientMaybe() {
  return useRecoilValue(missionsClientSelector);
}

export function useMissionGroups(): Array<UserMissionGroup> {
  const context = useRecoilValue(missionsContextAtom);
  return context?.user?.groups ?? [];
}

export function useConnectMissions() {
  const { missionApiKey } = useAuthenticated();
  const setContext = useSetRecoilState(missionsContextAtom);
  const setMissionsState = useSetRecoilState(missionsStateAtom);

  useEffect(() => {
    if (missionApiKey) {
      connectMissions(missionApiKey)
        .then((c) => {
          log.info('Missions API initialized.');
          setContext(c);
        })
        .catch((error) => {
          log.error('Error initializing missions API:', error);
          setMissionsState({ state: 'failed', error });
        });
    } else {
      log.info('No mission API key, skipping connection.');
      setContext(undefined);
    }
  }, [missionApiKey, setContext, setMissionsState]);
}

function createMissionsApi(apiKey: string): MissionsApi {
  return new MissionsApi({
    BASE: missionsServer,
    HEADERS: {
      authorization: apiKey,
      'user-agent': `rescuetablet/${platform}/${version}`,
    },
  });
}

export function useSubscribeMissions() {
  const context = useRecoilValue(missionsContextAtom);
  const setMissionsState = useSetRecoilState(missionsStateAtom);
  const toMission = useFromFirebaseMission();
  const setMissions = useSetMissions();

  useEffect(() => {
    if (!context) return;

    const groups = getSubscriptionGroups(context.user);
    if (!groups.length) {
      log.info('User does not have read permissions for any mission groups, skipping subscription.');
      setMissionsState({ state: 'disabled' });
      return;
    }

    const cleanups = groups.map(({ groupId, name }) => {
      log.info(`Subscribing to missions in group ${groupId} (${name ?? 'no name'})`);
      const firestore = getFirestore(context.firebase);
      const coll = collection(firestore, 'missionGroups', groupId, 'missions');

      return onSnapshot(
        coll,
        { includeMetadataChanges: true },
        (snapshot) => {
          log.debug(`Received missions snapshot in group ${groupId}.`);
          const missions = snapshot.docs.map((doc) => toMission(doc.id, doc.data() as FirebaseMissionData, groupId, doc.metadata.fromCache));
          setMissions((existing) => ({ ...existing, [groupId]: missions }));
          setMissionsState({ state: 'ready' });
        },
        (error) => {
          log.error(`Error subscribing to mission group ${groupId}:`, error);
          setMissionsState({ state: 'failed', error });
        }
      );
    });

    return () => {
      cleanups.forEach((c) => c());
    };
  }, [context, setMissions, setMissionsState, toMission]);
}

export function useSubscribeMissionEvents({ id: missionId, groupId }: Pick<FullMission, 'id' | 'groupId'>) {
  const context = useRecoilValue(missionsContextAtom);
  const setEvents = useSetMissionEvents(missionId);

  useEffect(() => {
    if (!context) return;
    log.info(`Subscribing to events of mission ${missionId} in group ${groupId}`);

    const firestore = getFirestore(context.firebase);
    const coll = collection(firestore, 'missionGroups', groupId, 'missions', missionId, 'events');

    return onSnapshot(
      coll,
      (snapshot) => {
        setEvents(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id } as MissionEvent & Identifiable)));
      },
      (error) => {
        // FIXME: error handling
        log.error('Error:', error);
      }
    );
  }, [context, missionId, groupId, setEvents]);
}

export function useAddMission() {
  const context = useRecoilValue(missionsContextAtom);
  const toMission = useFromFirebaseMission();
  const setMissions = useSetMissions();

  return useCallback(
    ({ id, ...data }: any) => {
      if (!context) return;
      (data.groupIds as string[]).forEach((groupId) => {
        const mission = toMission(id, data, groupId);
        setMissions((existing) => ({ ...existing, [groupId]: [...(existing[groupId] || []), mission] }));
      });
    },
    [context, toMission, setMissions]
  );
}

export function useRemoveMission() {
  const setMissions = useSetMissions();

  return useCallback(
    (id: string) => {
      setMissions((existing) => Object.fromEntries(Object.entries(existing).map(([groupId, missions]) => [groupId, missions.filter((m) => m.id !== id)])));
    },
    [setMissions]
  );
}

export function useUpdateMissionById() {
  const setMissions = useSetMissions();
  const toMission = useToMission();

  return useCallback(
    (missionId: string, updater: (mission: Mission) => Mission) => {
      setMissions((current) =>
        Object.fromEntries(
          Object.entries(current).map(([groupId, missions]) => [groupId, missions.map((m) => (m.id === missionId ? toMission(updater(m), groupId) : m))])
        )
      );
    },
    [setMissions, toMission]
  );
}

type FirebaseMissionData = Omit<Mission, 'id' | 'resources' | 'actions' | 'reports' | 'mapElements' | 'sections' | 'patients'> & {
  resources?: Record<string, Omit<Resource, 'id'>>;
  actions?: Record<string, Omit<Action, 'id'>>;
  reports?: Record<string, Omit<Report, 'id'>>;
  mapElements?: Record<string, Omit<MapElement, 'id'>>;
  sections?: Record<string, Omit<Section, 'id'>>;
  patients?: Record<string, Omit<Patient, 'id'>>;
};

function useFromFirebaseMission(): (id: string, data: FirebaseMissionData, groupId: string, stale?: boolean) => FullMission {
  const toMission = useToMission();

  return useCallback(
    (id, data, groupId, stale) =>
      toMission(
        {
          ...data,
          id,
          resources: Object.entries(data.resources ?? {}).map(([id, resource]: [string, any]) => ({ ...resource, id })),
          actions: Object.entries(data.actions ?? {}).map(([id, action]: [string, any]) => ({ ...action, id })),
          reports: Object.entries(data.reports ?? {}).map(([id, report]: [string, any]) => ({ ...report, id })),
          mapElements: Object.entries(data.mapElements ?? {}).map(([id, element]: [string, any]) => ({ id, ...element })),
          sections: Object.entries(data.sections ?? {}).map(([id, element]: [string, any]) => ({ id, ...element })),
          patients: Object.entries(data.patients ?? {}).map(([id, element]: [string, any]) => ({ id, ...element })),
        },
        groupId,
        stale
      ),
    [toMission]
  );
}

function useToMission(): (data: Mission, groupId: string, stale?: boolean) => FullMission {
  const context = useRecoilValue(missionsContextAtom);

  return useCallback(
    (data, groupId, stale = false) => {
      if (!context) throw new Error('Mission context not initialized!');
      const editable = context.user.groupPermissions.some((g) => g.groupId === groupId && g.permission === 'update');

      const mission: FullMission = {
        ...data,
        editable: editable && data.editable,
        stale,
        sections: [],
        groupId,
      };

      mission.sections = data.sections.map((section) => createSection(section, mission));

      return mission;
    },
    [context]
  );
}

function createSection(section: Omit<Section, 'resources'>, { resources, actions, reports }: SectionResources): SectionWithResources {
  return {
    ...section,
    resources: resources.filter((e) => e.sections?.includes(section.id)),
    actions: actions.filter((e) => e.sections?.includes(section.id)),
    reports: reports.filter((e) => e.sections?.includes(section.id)),
  };
}

async function connectMissions(apiKey: string): Promise<MissionsContext> {
  log.info('Connecting missions API.');
  const [user, firebase] = await Promise.all([getUserInfo(apiKey), initializeFirebase(apiKey)]);
  return { user, apiKey, firebase };
}

async function initializeFirebase(apiKey: string) {
  const appName = 'missions';
  const { config, token } = await getFirebaseConfig(apiKey);
  const firebase = initializeApp(config, appName);

  const auth = getAuth(firebase);
  await signInWithCustomToken(auth, token);

  enableIndexedDbPersistence(getFirestore(firebase));

  return firebase;
}

async function getFirebaseConfig(apiKey: string) {
  log.debug('Loading firebase config...');
  const config = await createMissionsApi(apiKey).user.getFirebaseConfig();
  log.debug(`Loaded firebase config.`);
  return config;
}

async function getUserInfo(apiKey: string): Promise<UserInfo> {
  log.debug('Loading user info...');
  const user = await createMissionsApi(apiKey).user.user();
  log.debug(`Loaded user info.`, user);
  return user;
}

function getSubscriptionGroups(user: UserInfo): Array<UserMissionGroup> {
  return user.groups.filter((p) => p.permissions.includes('read')).sort();
}
